import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { CredenciaisApi, PermissoesCredencialApi, PlanosApi, TabelaPropriaApi } from '@/services/api-routes'
import { emptyPage } from '@/types/api'
import type {
  CredencialResumo,
  ListarCredenciaisFiltro,
  PlanoCredencial,
  ProcedimentoTabelaPropria,
} from '@/types/credencial'
import { credenciaisKeys } from '@/http/queryKeys'
import { defaultOptions } from '@/http/default-query-options'

export const useCredenciaisQuery = (filtro: ListarCredenciaisFiltro, enabled = true) =>
  useQuery({
    queryKey: credenciaisKeys.list(filtro),
    queryFn: async ({ signal }) =>
      (await CredenciaisApi.listar(filtro, signal)) ?? emptyPage<CredencialResumo>(),
    placeholderData: keepPreviousData,
    enabled,
    ...defaultOptions,
  })

export const useCredencialQuery = (credenciaisId: string | undefined) =>
  useQuery({
    queryKey: credenciaisKeys.detail(credenciaisId ?? ''),
    queryFn: ({ signal }) => CredenciaisApi.obter(credenciaisId as string, signal),
    enabled: Boolean(credenciaisId),
    ...defaultOptions,
  })

export const usePermissoesQuery = (credenciaisId: string | undefined) =>
  useQuery({
    queryKey: credenciaisKeys.permissoes(credenciaisId ?? ''),
    queryFn: async ({ signal }) => {
      const dados = await PermissoesCredencialApi.obter(credenciaisId as string, signal)
      return {
        usuarios: dados?.usuarios ?? [],
        unidades: dados?.unidades ?? [],
        pontosAtendimento: dados?.pontosAtendimento ?? [],
      }
    },
    enabled: Boolean(credenciaisId),
    ...defaultOptions,
  })

export const usePlanosQuery = (
  credenciaisId: string | undefined,
  filtro: { pageNumber: number; pageSize: number },
) =>
  useQuery({
    queryKey: credenciaisKeys.planos(credenciaisId ?? '', filtro),
    queryFn: async ({ signal }) =>
      (await PlanosApi.listar(
        credenciaisId as string,
        filtro.pageNumber,
        filtro.pageSize,
        signal,
      )) ?? emptyPage<PlanoCredencial>(),
    enabled: Boolean(credenciaisId),
    placeholderData: keepPreviousData,
    ...defaultOptions,
  })

export const useTabelaPropriaQuery = (
  credenciaisId: string | undefined,
  filtro: { busca: string; pageNumber: number; pageSize: number },
) =>
  useQuery({
    queryKey: credenciaisKeys.tabelaPropria(credenciaisId ?? '', filtro),
    queryFn: async ({ signal }) =>
      (await TabelaPropriaApi.listar(
        credenciaisId as string,
        filtro.busca,
        filtro.pageNumber,
        filtro.pageSize,
        signal,
      )) ?? emptyPage<ProcedimentoTabelaPropria>(),
    enabled: Boolean(credenciaisId),
    placeholderData: keepPreviousData,
    ...defaultOptions,
  })
